import React, { useState } from 'react';
import {
  Grid, Stack, Alert,
  IconButton, InputAdornment, FormControl, FormControlLabel, InputLabel, Select, MenuItem, Chip,
  Checkbox, OutlinedInput, Box as MuiBox
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import baseurl from '../Baseurl/baseurl';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CancelIcon from '@mui/icons-material/Cancel';
import { Card, Eyebrow, Button, FormField, Input } from '../ui';

const AddCommunityAdmin = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    roles: [] // Array of objects: { roleName: string, permissions: string[] }
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const roleOptions = [
    'Member Management',
    'Category',
    'Business Management',
    'Family Information',
    'Referral System',
    'Review Testimonials',
  ];

  const permissionOptions = {
    'Member Management': ['add', 'edit', 'view', 'delete'],
    'Category': ['add', 'edit', 'view', 'delete'],
    'Business Management': ['add', 'edit', 'view', 'delete'],
    'Family Information': ['add', 'edit', 'view', 'delete'],
    'Referral System': [], // This is a boolean permission
    'Review Testimonials': [] // This is a boolean permission
  };

  // Format role name and permissions into a string
  const formatRoleString = (roleName, permissions) => {
    if (permissions.length === 0) return roleName;
    return `${roleName} -- ${permissions.join(', ')}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Handle role selection change
  const handleRoleChange = (event) => {
    const {
      target: { value },
    } = event;

    // Convert selected values to role objects
    const newRoles = value.map(roleName => {
      // Check if this role already exists in the form
      const existingRole = form.roles.find(r => r.roleName === roleName);
      if (existingRole) {
        return existingRole;
      }
      // If it's a new role, initialize with default permissions
      return {
        roleName,
        permissions: permissionOptions[roleName] && permissionOptions[roleName].length > 0
          ? ['view'] // Default to view permission
          : [] // For boolean permissions, no specific permissions needed
      };
    });

    setForm((prev) => ({
      ...prev,
      roles: newRoles
    }));
  };

  // Handle permission change for a specific role
  const handlePermissionChange = (roleName, permission) => {
    setForm((prev) => {
      const updatedRoles = prev.roles.map(role => {
        if (role.roleName === roleName) {
          // Toggle the permission
          const updatedPermissions = role.permissions.includes(permission)
            ? role.permissions.filter(p => p !== permission)
            : [...role.permissions, permission];

          return {
            ...role,
            permissions: updatedPermissions
          };
        }
        return role;
      });

      return {
        ...prev,
        roles: updatedRoles
      };
    });
  };

  // Handle role removal
  const handleRoleDelete = (roleToDelete) => {
    setForm((prev) => ({
      ...prev,
      roles: prev.roles.filter((role) => role.roleName !== roleToDelete)
    }));
  };

  // Reset form to initial state
  const handleReset = () => {
    setForm({
      username: '',
      email: '',
      password: '',
      roles: [] // Reset to empty array
    });
    setError('');
    setSuccess('');
    setShowPassword(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      // Convert roles back to string format
      const roleStrings = form.roles.map(role =>
        formatRoleString(role.roleName, role.permissions)
      );

      const res = await fetch(`${baseurl}/api/community_admin/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: form.username,
          email: form.email,
          password: form.password,
          role: roleStrings
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to add community admin');
      setSuccess('Community Admin created successfully');
      setTimeout(() => navigate('/admin/CommunityAdmin'), 800);
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page head */}
      <div className="mb-5 flex flex-col gap-3 border-b border-line/10 pb-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Eyebrow>Admins</Eyebrow>
          <h1 className="mt-2 font-display text-3xl font-bold leading-tight tracking-tight text-ink">
            Add <em className="kbc-serif">Community</em> Admin
          </h1>
          <p className="mt-1 max-w-prose text-sm leading-relaxed text-text">
            Create an admin account and grant module-level permissions.
          </p>
        </div>
        <Button variant="ghost" onClick={() => navigate('/admin/CommunityAdmin')}>
          Back to List
        </Button>
      </div>

      <Card padding="lg">
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormField label="Username" htmlFor="username" required>
                <Input
                  id="username"
                  name="username"
                  value={form.username}
                  onChange={handleChange}
                  required
                />
              </FormField>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormField label="Email" htmlFor="email" required>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </FormField>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormField label="Password" htmlFor="password" required>
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={handleChange}
                  required
                  rightIcon={
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword((prev) => !prev)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowPassword((prev) => !prev); }}
                      className="pointer-events-auto cursor-pointer text-muted hover:text-ink"
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </span>
                  }
                />
              </FormField>
            </Grid>

            {/* Role Dropdown - Multiple Selection */}
            <Grid size={{ xs: 12, md: 6 }}>
              <label className="mb-2 block text-[0.82rem] font-semibold text-ink">
                Roles<span className="ml-1 text-danger">*</span>
              </label>
              <FormControl
                fullWidth
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'transparent',
                    borderRadius: '14px',
                    color: 'text.primary',
                  },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                }}
              >
                <Select
                  multiple
                  displayEmpty
                  value={form.roles.map(role => role.roleName)}
                  onChange={handleRoleChange}
                  input={<OutlinedInput />}
                  renderValue={(selected) =>
                    selected.length === 0 ? (
                      <span style={{ opacity: 0.6 }}>Select roles</span>
                    ) : (
                      <MuiBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={value}
                            onDelete={() => handleRoleDelete(value)}
                            deleteIcon={
                              <IconButton
                                size="small"
                                onMouseDown={(event) => event.stopPropagation()}
                                sx={{ color: 'inherit' }}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            }
                            mouseLeaveDelay={500}
                            sx={{
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              fontWeight: 600,
                            }}
                          />
                        ))}
                      </MuiBox>
                    )
                  }
                >
                  {roleOptions.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Permissions for each selected role */}
          <div className="mt-7">
            <h3 className="mb-3 font-display text-base font-semibold tracking-tight text-ink">
              Permissions
            </h3>

            {form.roles.length > 0 ? (
              <Grid container spacing={2}>
                {form.roles.map((role) => (
                  <Grid size={12} key={role.roleName}>
                    <div className="rounded-md border border-line/15 bg-surface/60 p-4 transition-shadow hover:shadow-card">
                      <h4 className="mb-3 font-display text-base font-semibold text-primary">
                        {role.roleName}
                      </h4>

                      {permissionOptions[role.roleName] && permissionOptions[role.roleName].length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {permissionOptions[role.roleName].map((permission) => {
                            const active = role.permissions.includes(permission);
                            return (
                              <Chip
                                key={permission}
                                label={permission}
                                clickable
                                onClick={() => handlePermissionChange(role.roleName, permission)}
                                variant={active ? 'filled' : 'outlined'}
                                sx={{
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                  textTransform: 'capitalize',
                                  bgcolor: active ? 'primary.main' : 'transparent',
                                  color: active ? 'primary.contrastText' : 'text.primary',
                                  borderColor: active ? 'primary.main' : 'divider',
                                  '&:hover': {
                                    bgcolor: active ? 'primary.dark' : 'action.hover',
                                  },
                                }}
                              />
                            );
                          })}
                        </div>
                      ) : (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={role.permissions.length > 0}
                              onChange={() => handlePermissionChange(role.roleName, 'access')}
                              sx={{ color: 'text.secondary', '&.Mui-checked': { color: 'primary.main' } }}
                            />
                          }
                          label="Grant Access"
                          sx={{ mt: 1, color: 'text.primary' }}
                        />
                      )}
                    </div>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <div className="rounded-md border border-dashed border-line/20 bg-surface/40 px-6 py-8 text-center">
                <p className="text-sm text-muted">
                  No roles selected. Please select roles to configure permissions.
                </p>
              </div>
            )}
          </div>

          <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end', mt: 4 }}>
            <Button type="button" variant="ghost" onClick={handleReset}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={submitting} disabled={submitting}>
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mt: 3 }}>{success}</Alert>
          )}
        </form>
      </Card>
    </div>
  );
};

export default AddCommunityAdmin;
